import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import './Filter.scss';
import { MainContext } from "../Contexts/MainContext";
import { useContextData } from '../ApiContext';
import {
    CASE_TYPES,
    DEVICES,
    CASE_BRANDS,
    CASE_COLORS,
    PRICE_RANGES,
    buildPhoneCaseQueryParams,
    DEFAULT_QUERY_PARAMS,
} from '../Products/data/phoneCaseFilters';

const FILTER_SECTIONS = [
    { id: 'price', label: 'Price' },
    { id: 'caseType', label: 'Case Type', options: CASE_TYPES, draftKey: 'caseType' },
    { id: 'device', label: 'Device', options: DEVICES, draftKey: 'subcategory' },
    { id: 'brand', label: 'Brand', options: CASE_BRANDS, draftKey: 'brand' },
    { id: 'color', label: 'Color', options: CASE_COLORS, draftKey: 'color' },
];

function priceMatchesRange(price, range) {
    return Number(price?.[0]) === range.min && Number(price?.[1]) === range.max;
}

function snapshotQueryParams(queryParams) {
    return buildPhoneCaseQueryParams({
        caseType: queryParams.caseType || [],
        subcategory: queryParams.subcategory || [],
        brand: queryParams.brand || [],
        color: queryParams.color || [],
        price: Array.isArray(queryParams.price) ? queryParams.price : DEFAULT_QUERY_PARAMS.price,
        sort: queryParams.sort,
    });
}

export function Filter({ isOverlay = false, onClose }) {
    const location = useLocation();
    const [selected, setSelected] = useState("caseType");
    const navigate = useNavigate();

    const { setLockFooter } = useContext(MainContext);
    const { queryParams, setQueryParams } = useContextData();

    const initialQueryParamsRef = useRef(null);
    useEffect(() => {
        if (initialQueryParamsRef.current === null) {
            initialQueryParamsRef.current = snapshotQueryParams(queryParams);
        }
    }, []);

    const [draft, setDraft] = useState(() => snapshotQueryParams(queryParams));

    useEffect(() => {
        setLockFooter(true);
        return () => {
            setLockFooter(false);
        };
    }, [setLockFooter]);

    const toggleMulti = (key, value) => {
        setDraft((prev) => {
            const current = Array.isArray(prev[key]) ? [...prev[key]] : [];
            const exists = current.includes(value);
            const nextValues = exists
                ? current.filter((item) => item !== value)
                : [...current, value];

            return buildPhoneCaseQueryParams({
                caseType: key === 'caseType' ? nextValues : (prev.caseType || []),
                subcategory: key === 'subcategory' ? nextValues : (prev.subcategory || []),
                brand: key === 'brand' ? nextValues : (prev.brand || []),
                color: key === 'color' ? nextValues : (prev.color || []),
                price: prev.price,
                sort: prev.sort,
            });
        });
    };

    /** Single-select price bucket; tap again to clear back to full range. */
    const selectPriceRange = (range) => {
        setDraft((prev) => {
            const nextPrice = priceMatchesRange(prev.price, range)
                ? [...DEFAULT_QUERY_PARAMS.price]
                : [range.min, range.max];
            return buildPhoneCaseQueryParams({
                caseType: prev.caseType || [],
                subcategory: prev.subcategory || [],
                brand: prev.brand || [],
                color: prev.color || [],
                price: nextPrice,
                sort: prev.sort,
            });
        });
    };

    function fetchSelectedData() {
        if (selected === 'price') {
            return PRICE_RANGES.map((range) => {
                const isActive = priceMatchesRange(draft.price, range);
                const inputId = `filter-price-${range.id}`;
                return (
                    <label
                        key={range.id}
                        htmlFor={inputId}
                        className={`simpleInfo${isActive ? ' is-selected' : ''}`}
                    >
                        <input
                            id={inputId}
                            type="radio"
                            name="filter-price-range"
                            className="filter-option-checkbox"
                            checked={isActive}
                            onChange={() => selectPriceRange(range)}
                            onClick={() => {
                                if (isActive) selectPriceRange(range);
                            }}
                        />
                        <span className="filter-option-label">{range.label}</span>
                    </label>
                );
            });
        }

        const section = FILTER_SECTIONS.find((item) => item.id === selected);
        if (!section?.options) return null;

        const selectedValues = Array.isArray(draft[section.draftKey])
            ? draft[section.draftKey]
            : [];

        return section.options.map((option) => {
            const isActive = selectedValues.includes(option);
            const inputId = `filter-${section.draftKey}-${option.replace(/\s+/g, '-')}`;
            return (
                <label
                    key={option}
                    htmlFor={inputId}
                    className={`simpleInfo${isActive ? ' is-selected' : ''}`}
                >
                    <input
                        id={inputId}
                        type="checkbox"
                        className="filter-option-checkbox"
                        checked={isActive}
                        onChange={() => toggleMulti(section.draftKey, option)}
                    />
                    <span className="filter-option-label">{option}</span>
                </label>
            );
        });
    }

    const navigateBack = (from, scrollY) => {
        if (typeof from === 'string') {
            navigate(from, { state: { restoreScroll: scrollY ?? 0 } });
        } else {
            navigate(-1);
        }
    };

    const handleCancel = () => {
        if (initialQueryParamsRef.current) {
            setQueryParams(initialQueryParamsRef.current);
        }
        if (isOverlay && typeof onClose === 'function') {
            onClose();
            return;
        }
        navigateBack(location.state?.from, location.state?.scrollY);
    };

    const handleApply = () => {
        setQueryParams(
            buildPhoneCaseQueryParams({
                caseType: draft.caseType,
                subcategory: draft.subcategory,
                brand: draft.brand,
                color: draft.color,
                price: draft.price,
                sort: draft.sort,
            })
        );
        if (isOverlay && typeof onClose === 'function') {
            onClose();
            requestAnimationFrame(() => {
                window.dispatchEvent(new CustomEvent('mob-products-filter-applied'));
            });
            return;
        }
        navigateBack(location.state?.from, location.state?.scrollY);
    };

    const handleClearAll = () => {
        setDraft((prev) => ({
            ...DEFAULT_QUERY_PARAMS,
            price: [0, 10000],
            ...(prev.sort != null && { sort: prev.sort }),
        }));
    };

    return (
        <div className="searchFilters">
            <div className="grid filterHeader">
                <div className="col-1-2-A">
                    <div className="close-block">
                        <span className="close-label">FILTERS</span>
                    </div>
                </div>
                <div className="col-1-2-B">
                    <div className="ripple-container ">
                        <button type="button" className="btn default btn primary flat filters-clear" onClick={handleClearAll}>
                            Clear All
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid filters-content">
                <div className="col-1-3 filters">
                    <div className="filtersPane">
                        <div className="side-container">
                            <div className="left-side">
                                {FILTER_SECTIONS.map((section) => (
                                    <button
                                        type="button"
                                        key={section.id}
                                        className={selected === section.id ? 'active' : ''}
                                        onClick={() => setSelected(section.id)}
                                    >
                                        {section.label}
                                    </button>
                                ))}
                            </div>
                            <div className="right-side">
                                {fetchSelectedData()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid filterHeader bottom-filter">
                <div className="col-1-2-C">
                    <div className="ripple-container ">
                        <button type="button" className="btn default btn primary flat filters-clear" onClick={handleCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
                <div className="col-1-2-B filter-center">
                    <div className="ripple-container ">
                        <button type="button" className="btn default btn primary flat filters-clear" onClick={handleApply}>
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
